import React, { useRef, useState, useCallback } from "react";
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { useThemeColor } from "../hooks/useThemeColor";
import { sendMobileOtp, verifyMobileOtp, parsePhoneForOtp } from "./payAtTableApi";

const DIGITS = 4;
const RESEND_COOLDOWN_SEC = 60;

function maskPhone(phone) {
  if (!phone || phone.length < 8) return phone;
  const cleaned = phone.replace(/\D/g, "");
  const len = cleaned.length;
  if (len <= 4) return "+" + cleaned.slice(-4).padStart(4, "*");
  const lastTwo = cleaned.slice(-2);
  const firstPart = cleaned.slice(0, Math.min(2, len - 4));
  const mid = "*".repeat(Math.max(0, len - 4 - firstPart.length));
  return `+${firstPart}${mid} ${lastTwo}`;
}

export default function OTPVerify({ phone, onVerify, onResend }) {
  const { themeColor } = useThemeColor();
  const { endpoint } = useSelector((state) => state.auth);
  const [digits, setDigits] = useState(Array(DIGITS).fill(""));
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);
  const primaryColor = themeColor || "#162A5C";

  const masked = maskPhone(phone || "");

  const setDigit = useCallback((index, value) => {
    if (value !== "" && !/^\d$/.test(value)) return;
    setDigits((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setError("");
    if (value && index < DIGITS - 1) {
      setTimeout(() => inputRefs.current[index + 1]?.focus(), 0);
    }
  }, []);

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      setDigit(index - 1, "");
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData?.getData("text") || "").replace(/\D/g, "").slice(0, DIGITS);
    const arr = pasted.split("");
    const next = [...digits];
    arr.forEach((c, i) => {
      if (i < DIGITS) next[i] = c;
    });
    setDigits(next);
    setError("");
    const focusIdx = Math.min(pasted.length, DIGITS - 1);
    setTimeout(() => inputRefs.current[focusIdx]?.focus(), 0);
  };

  const code = digits.join("");
  const canVerify = code.length === DIGITS;

  const handleVerify = async () => {
    if (!canVerify || !endpoint || !phone) return;
    setLoading(true);
    setError("");
    try {
      const { country_code, mobile_no } = parsePhoneForOtp(phone);
      await verifyMobileOtp(endpoint, { country_code, mobile_no, otp: code });
      onVerify();
    } catch (err) {
      setError(err?.message || "Invalid code. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !endpoint || !phone) return;
    setResendCooldown(RESEND_COOLDOWN_SEC);
    const id = setInterval(() => {
      setResendCooldown((c) => {
        if (c <= 1) {
          clearInterval(id);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    setError("");
    try {
      const { country_code, mobile_no } = parsePhoneForOtp(phone);
      await sendMobileOtp(endpoint, { country_code, mobile_no });
    } catch (err) {
      setError(err?.message || "Error sending OTP");
      setResendCooldown(0);
    }
  };

  return (
    <Container maxWidth="sm" className="pay-at-table-step" sx={{ py: 1 }}>
      <Paper
        elevation={2}
        sx={{ p: { xs: 2.5, sm: 3 }, borderRadius: 2 }}
        className="pay-at-table-card"
      >
        <Box sx={{ textAlign: "left", mb: 2.5 }}>
          <Typography
            variant="overline"
            sx={{ letterSpacing: 1.2 }}
            color="text.secondary"
          >
            Step 3 · One-time code
          </Typography>
          <Typography
            variant="h5"
            fontWeight={700}
            gutterBottom
            color="text.primary"
          >
            Enter verification code
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We’ve sent a 6-digit code to {masked || "+44 7*** *** 89"}. Enter it
            below to continue.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "center",
            mb: 2,
            flexWrap: "wrap",
          }}
          onPaste={handlePaste}
        >
          {Array.from({ length: DIGITS }).map((_, i) => (
            <input
              key={i}
              ref={(el) => (inputRefs.current[i] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digits[i]}
              onChange={(e) => setDigit(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              className="pay-at-table-otp-input"
              style={{
                width: 44,
                height: 48,
                textAlign: "center",
                fontSize: "1.25rem",
                fontWeight: 600,
                border: `2px solid ${digits[i] ? primaryColor : "#ccc"}`,
                borderRadius: 8,
                outline: "none",
                transition: "border-color 0.2s, box-shadow 0.2s",
              }}
            />
          ))}
        </Box>

        {error && (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {error}
          </Typography>
        )}

        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={handleResend}
          disabled={resendCooldown > 0}
          sx={{
            border: "none",
            background: "none",
            cursor: resendCooldown > 0 ? "not-allowed" : "pointer",
            color: resendCooldown > 0 ? "text.disabled" : "primary.main",
            textDecoration: "underline",
            mb: 2,
            p: 0,
          }}
        >
          {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Resend code"}
        </Typography>

        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!canVerify || loading}
          onClick={handleVerify}
          sx={{
            bgcolor: primaryColor,
            color: "#fff",
            py: 1.5,
            "&:hover": { bgcolor: primaryColor, filter: "brightness(1.1)" },
          }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Verify & Continue"
          )}
        </Button>
      </Paper>
    </Container>
  );
}
